import { Injectable } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { take, takeWhile, timer } from "rxjs";
import { AlertService } from "src/app/main/services/alert/alert.service";
import * as uuid from "uuid";
import { FormInstance } from "../../model/forms/form-instance.model";
import { FormInstanceService } from "../form-instance.service";
import { FormResponseService } from "../form-response.service";
import { FormGroupCompanyService } from "../forms/form-group-company.service";
import { ToastService } from "../toast.service";

export enum FormCompletionStates {
  Intro = "Intro",
  Progress = "Progress",
  ThankYou = "ThankYou",
}

@Injectable({
  providedIn: null,
})
export class FormCompletionService {
  public formGroup: FormGroup;
  public isLoadingFormInstance: boolean = true;
  public isSubmitting: boolean = false;
  public showErrors: boolean = false;
  public formInstance: FormInstance;
  public currentState: FormCompletionStates = FormCompletionStates.Intro;

  private submissionUUID: string = uuid.v4();
  private formInstanceUUID: string | null;
  private formTimerSubscription: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formInstanceService: FormInstanceService,
    private formResponseService: FormResponseService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private formGroupCompanyService: FormGroupCompanyService,
    private alertService: AlertService,
  ) {
    this._fetchFormInstance();
  }

  public startFormInstance() {
    this.currentState = FormCompletionStates.Progress;
    const formTimer = (this.formInstance.formTemplate.timerInMinutes || 0) * 60 * 1000;
    const alertBeforeEndFormTimer =
      (this.formInstance.formTemplate.alertBeforeEndTimerInMinutes || 0) * 60 * 1000;

    if (formTimer) {
      this.formTimerSubscription = timer(
        formTimer - alertBeforeEndFormTimer,
        alertBeforeEndFormTimer,
      )
        .pipe(
          take(2),
          takeWhile(() => this.currentState !== FormCompletionStates.ThankYou),
        )
        .subscribe({
          next: (it) => {
            if (it === 0 && alertBeforeEndFormTimer) {
              this.toastService.showWarning(
                `Warning: You have only ${this.formInstance.formTemplate.alertBeforeEndTimerInMinutes} minutes remaining to finish this survey, or you will have to restart from the beginning.`,
                undefined,
                { timeOut: 10000 },
              );
            } else if (it === 1) {
              this.toastService.showWarning("Time expired. Please restart survey.");
              this.restartFormInstance();
            }
          },
        });
    }
  }

  public restartFormInstance() {
    if (this.formTimerSubscription) {
      this.formTimerSubscription.unsubscribe();
    }
    if (this.formGroup) {
      this.formGroup.reset();
    }
    this.currentState = FormCompletionStates.Intro;
    this.submissionUUID = uuid.v4();
    this.showErrors = false;
  }

  public submitFormInstance() {
    this.showErrors = true;
    const formResponse = this.formInstance.collectAnswers(this.formGroup);

    if (!this.formGroup.valid) {
      return;
    }

    this.isSubmitting = true;
    this.formResponseService
      .create({
        ...formResponse,
        submissionUUID: this.submissionUUID,
      })
      .subscribe({
        next: async () => {
          this.isSubmitting = false;
          this.formGroup.reset();
          this.currentState = FormCompletionStates.ThankYou;
        },
        error: (error) => {
          this.isSubmitting = false;
          this.toastService.showError(error.error.message);
          Sentry.captureException(error);
        },
      });
  }

  private async _fetchFormInstance() {
    this.formInstanceUUID = this.route.snapshot.paramMap.get("id");

    if (
      this.route.snapshot.url.length === 2 &&
      this.route.snapshot.url[0].path.toLowerCase() === "s1" &&
      !!this.route.snapshot.url[1]
    ) {
      // this URL has been redirected from an S1 URL.  We need to get the migrated FormInstanceId from the PSID in the URL
      this.formInstanceUUID = await this._extractFormInstanceUUIDFromS1PSID(
        this.route.snapshot.url[1].path,
      );
    }

    if (!this.formInstanceUUID) {
      this.router.navigateByUrl("/404", { skipLocationChange: true });
    } else {
      this.isLoadingFormInstance = true;
      try {
        const formInstance = await this.formInstanceService.getFormInstanceByUUID(
          this.formInstanceUUID,
          false,
        );
        if (!formInstance.canBeSubmitted) {
          // If the form instance is a placeholder for a CFET, we don't want to let the user fill it out
          this.router.navigateByUrl("/404", { skipLocationChange: true });
          return;
        }

        this._initFormInstance(formInstance);
        if (formInstance.formTemplate.allowCobranding) {
          await this._setSignedLogoUrl();
        }
        this.isLoadingFormInstance = false;
      } catch (error) {
        if (error.statusCode === 404) {
          this.router.navigateByUrl("/404", { skipLocationChange: true });
        } else {
          Sentry.captureException(error);
        }
      }
    }
  }

  private _initFormInstance(formInstance: FormInstance) {
    this.formInstance = formInstance;
    this.formGroup = this.formBuilder.group(this.formInstance.formTemplate.collectFormControls());
  }

  /**
   * Set the signed logo URL for the formGroupCompany if one exists
   */
  private async _setSignedLogoUrl() {
    if (this.formInstance.formGroupInstance.companyWithOnlineLogo) {
      await this.formInstance.formGroupInstance.companyWithOnlineLogo.setSignedLogoUrl(
        this.formGroupCompanyService,
      );
    }
  }

  private async _extractFormInstanceUUIDFromS1PSID(s1URL: string): Promise<string> {
    let uuid = "";

    try {
      if (s1URL) {
        const s1URLParts = s1URL.split("-");
        if (s1URLParts.length >= 2) {
          const psid = parseInt(s1URLParts[1]);
          if (isNaN(psid)) {
            throw new Error(`Invalid PSID for ${s1URL}`);
          }

          uuid = await this.formInstanceService.getFormInstanceByS1PSID(psid);
        }
      }

      return uuid;
    } catch (error) {
      Sentry.captureException(error);
      return uuid;
    }
  }
}
